import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSearchParams, Link } from "react-router-dom";
import { formatDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { CheckSquare, XSquare } from "@phosphor-icons/react";

export default function Compliance() {
  const [sp, setSp] = useSearchParams();
  const [practitioners, setPractitioners] = useState([]);
  const [data, setData] = useState(null);
  const selected = sp.get("practitioner") || "";

  useEffect(() => {
    api.get("/practitioners").then((r) => {
      setPractitioners(r.data);
      if (!selected && r.data.length > 0) setSp({ practitioner: r.data[0].id });
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/compliance/checklist/${selected}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [selected]);

  const grouped = data ? groupBySource(data.items) : {};

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Audit view</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Compliance checklist</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-3xl">
            Structured against Louisiana Medicaid (LA Dept of Health), Joint Commission ambulatory surgery standards,
            and NAMSS Ideal Credentialing Standards. Primary source verification tracked per item.
          </p>
        </div>
        <select
          value={selected}
          onChange={(e) => setSp({ practitioner: e.target.value })}
          className="border border-gray-300 bg-white px-3 py-2 text-sm min-w-[280px]"
          data-testid="compliance-practitioner-select"
        >
          {practitioners.map((p) => <option key={p.id} value={p.id}>{p.last_name}, {p.first_name} ({p.title})</option>)}
        </select>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {["LA Medicaid", "Joint Commission", "NAMSS"].map((src) => {
              const list = grouped[src] || [];
              const complete = list.filter((i) => i.present).length;
              return (
                <div key={src} className="bg-white border border-gray-200 p-5">
                  <div className="overline text-gray-500">{src}</div>
                  <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{complete}/{list.length}</div>
                  <div className="text-xs text-gray-500 mt-1">items present</div>
                </div>
              );
            })}
          </div>

          {Object.entries(grouped).map(([src, items]) => (
            <section key={src} className="bg-white border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="overline text-gray-500">Framework</div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">{src}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`compliance-table-${src.replace(/\s/g, '-').toLowerCase()}`}>
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                      <th className="px-6 py-3 font-semibold">Requirement</th>
                      <th className="px-4 py-3 font-semibold">Present</th>
                      <th className="px-4 py-3 font-semibold">Expires</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">PSV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.type} className="data-row border-b border-gray-100">
                        <td className="px-6 py-3 font-medium text-slate-900">{i.label}</td>
                        <td className="px-4 py-3">
                          {i.present
                            ? <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                            : <XSquare size={18} weight="fill" className="text-red-600" />}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{formatDate(i.expiration_date)}</td>
                        <td className="px-4 py-3">
                          {i.present ? <StatusBadge status={i.status} /> : <span className="status-pill status-missing">Missing</span>}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono">
                          {i.psv_status ? (
                            <span className={`inline-block px-2 py-0.5 ${i.psv_status === "VERIFIED" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>
                              {i.psv_status}
                            </span>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

function groupBySource(items) {
  const g = {};
  for (const i of items) {
    if (!g[i.source]) g[i.source] = [];
    g[i.source].push(i);
  }
  return g;
}
